import type { AiModelCreationFailedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelCreationFailed';
import type { AiModelUpdateFailedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelUpdateFailed';
import CreateAiModel from '@Application/Ai/AiConfiguration/Models/Commands/CreateAiModel';
import UpdateAiModel from '@Application/Ai/AiConfiguration/Models/Commands/UpdateAiModel';
import createAiModel from '@Application/Ai/AiConfiguration/Models/createAiModel';
import updateAiModel from '@Application/Ai/AiConfiguration/Models/updateAiModel';
import FetchAiModelFamilies from '@Application/Ai/AiModelFamilies/Queries/FetchAiModelFamilies';
import fetchAiModelFamilies from '@Application/Ai/AiModelFamilies/fetchAiModelFamilies';
import FetchAiTranscriptionDeliveryModes from '@Application/Ai/AiTranscriptionDeliveryModes/Queries/FetchAiTranscriptionDeliveryModes';
import fetchAiTranscriptionDeliveryModes from '@Application/Ai/AiTranscriptionDeliveryModes/fetchAiTranscriptionDeliveryModes';
import FetchAiUsages from '@Application/Ai/AiUsages/Queries/FetchAiUsages';
import fetchAiUsages from '@Application/Ai/AiUsages/fetchAiUsages';
import AzureSpeechSettingsSection from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/Sections/AzureSpeechSettingsSection';
import mapAzureSpeechFieldErrors from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/Helpers/mapAzureSpeechFieldErrors';
import {
	AZURE_SPEECH_CHUNK_MAX_SECONDS_MAX,
	AZURE_SPEECH_FAMILY,
	AZURE_SPEECH_KIND,
} from '@UI/Pages/Administration/AiEngines/azureSpeechValues';
import AUDIO_USAGES from '@UI/Pages/Administration/AiEngines/audioUsages';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FormSection from '@Guidelines/Component/FormSection';
import modelSidebarSignal, {
	closeModelSidebar,
} from '@UI/Pages/Administration/AiEngines/Signals/modelSidebarSignal';
import AiModelFormData from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/AiModelFormData';
import {
	REALTIME_DELIVERY_MODE,
	canServeRealtime,
} from '@UI/Pages/Administration/AiEngines/realtimeDeliveryRules';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import {
	Autocomplete,
	Button,
	Checkbox,
	Dropdown,
	Form,
	InputNumber,
	InputText,
	Message,
	MultiSelect,
	Sidebar,
	TextArea,
} from '@verclam/ui-kit';

/** Matches `AiProviderKind.AZURE_OPENAI` (`apps/backend/src/enums/ai/ai-provider-kind.enum.ts`). */
const AZURE_OPENAI_KIND = 'azure_openai';

// Keeps the wrapper out of the form layout: its child lays out as if the wrapper were absent.
const contentsStyle = css`
	display: contents;
`;

const helpStyle = css`
	margin: 0 0 0.5rem;
	padding-left: 0.75rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const toPriceInput = (value: number | null): string => (value === null ? '' : String(value));

const toPriceNumber = (value: string): number | null => (value === '' ? null : Number(value));

/**
 * One panel for both creation and edition, told apart by `modelId` on the signal's context. Reads the
 * edited model straight from the store rather than through a prop: the page mounts this component
 * once, with no data of its own to pass it.
 */
const ModelSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(modelSidebarSignal);
	const models = useSelector((state) => state.aiConfigurationStore.models);
	const providers = useSelector((state) => state.aiConfigurationStore.providers);
	const { user } = useStrictAuthenticatedUser();

	const [formData, setFormData] = React.useState(() => AiModelFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const { data: aiUsages } = useStoreQuery({
		selector: (state) => state.aiUsagesStore.aiUsages,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAiUsages(new FetchAiUsages())),
	});

	const { data: aiModelFamilies } = useStoreQuery({
		selector: (state) => state.aiModelFamiliesStore.aiModelFamilies,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAiModelFamilies(new FetchAiModelFamilies())),
	});

	const { data: aiTranscriptionDeliveryModes } = useStoreQuery({
		selector: (state) => state.aiTranscriptionDeliveryModesStore.aiTranscriptionDeliveryModes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAiTranscriptionDeliveryModes(new FetchAiTranscriptionDeliveryModes())),
	});

	const formId = React.useId();
	const modelId = context?.modelId ?? null;

	const editedModel = React.useMemo(() => {
		if (modelId === null || !models) {
			return null;
		}

		return models.findById(modelId);
	}, [modelId, models]);

	// `Autocomplete` never derives its displayed text from `value` on its own — only an explicit
	// selection does, and never a value set from a prop at mount (confirmed in `@base-ui/react`'s
	// `useValueChanged`, which skips its very first call). `identifierQuery` seeds the box for edit
	// mode; every handler below releases it back to `undefined` on the first real interaction, handing
	// display back to the field's own internal, uncontrolled query state — required for free typing to
	// work at all (a `query` left permanently controlled freezes the box, see `handleIdentifierChange`).
	const [identifierQuery, setIdentifierQuery] = React.useState<string | undefined>(undefined);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(
			editedModel !== null ? AiModelFormData.fromView(editedModel) : AiModelFormData.createEmpty(),
		);
		setIdentifierQuery(editedModel !== null ? editedModel.identifier : undefined);
	}, [context, editedModel]);

	const selectedProvider = React.useMemo(() => {
		return providers?.findById(formData.providerId) ?? null;
	}, [providers, formData.providerId]);

	// Locally filtered: the provider's discovered models (`AiProviderView.lastTestModels`) already sit
	// in the store, no network round trip needed to suggest them.
	const modelSuggestions = React.useMemo(() => {
		const availableModels = selectedProvider?.lastTestModels ?? [];
		const normalizedQuery = formData.identifier.trim().toLowerCase();

		if (normalizedQuery === '') {
			return availableModels;
		}

		return availableModels.filter((model) => model.toLowerCase().includes(normalizedQuery));
	}, [selectedProvider, formData.identifier]);

	const identifierHelp = React.useMemo(() => {
		let baseHelp = 'Le modèle tel qu’exposé par l’API du fournisseur (ex. gpt-4o-mini).';

		if (selectedProvider?.kind.value === AZURE_OPENAI_KIND) {
			baseHelp =
				'Saisissez le nom de votre déploiement Azure. La liste ci-dessous montre les modèles disponibles sur la ressource, pas vos déploiements.';
		} else if (selectedProvider?.kind.value === AZURE_SPEECH_KIND) {
			baseHelp =
				'Nom libre, par exemple azure-transcribe : c’est toujours le modèle standard Azure Transcribe qui répond.';
		}

		if (selectedProvider !== null && selectedProvider.lastTestModels.length === 0) {
			return `${baseHelp} Testez la connexion du fournisseur pour découvrir ses modèles.`;
		}

		return baseHelp;
	}, [selectedProvider]);

	// `family` and `deliveryMode` are only meaningful, and only required, for a model carrying at least
	// one audio usage (`AUDIO_USAGES`, duplicated from the server's own definition — see that file's docblock).
	const hasAudioUsage = React.useMemo(() => {
		return formData.usages.some((usage) => AUDIO_USAGES.includes(usage));
	}, [formData.usages]);

	// The real-time option is disabled, never silently rewritten: a `realtime` value that became
	// ineligible stays selected and is reported by the constraint or the server.
	const isDeliveryModeOptionDisabled = React.useMemo(() => {
		const isRealtimeEligible =
			selectedProvider !== null && canServeRealtime(selectedProvider.kind.value, formData.family);

		return (option: unknown): boolean => {
			return (
				option instanceof LabeledValueView &&
				option.value === REALTIME_DELIVERY_MODE &&
				!isRealtimeEligible
			);
		};
	}, [formData.family, selectedProvider]);

	const isAzureSpeechFamily = formData.family === AZURE_SPEECH_FAMILY;

	const handleProviderIdChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setProviderId(value));
	}, []);

	// `Autocomplete.onChange` only fires on an explicit selection (or its clearing): free typing never
	// reaches it, by design of the underlying combobox. `onSearch`/`onClear` below are what keep
	// `formData.identifier` in sync with free-typed text — the nominal path here, `forceSelection`
	// being deliberately absent (see the Azure case in the field's `subLabel`).
	const handleIdentifierChange = React.useCallback((value: string | readonly string[] | null) => {
		setIdentifierQuery(undefined);
		setFormData((current) => current.setIdentifier(typeof value === 'string' ? value : ''));
	}, []);

	// `delay={0}` and `dropdownMode="current"`: suggestions are a local, synchronous filter (no network
	// round trip), and the dropdown button must re-run this same local filter on the CURRENT text rather
	// than resetting the query to blank.
	const handleIdentifierSearch = React.useCallback((query: string) => {
		setIdentifierQuery(undefined);
		setFormData((current) => current.setIdentifier(query));
	}, []);

	// `Autocomplete` only calls `onSearch` after a timer that every keystroke cancels, so a seeded
	// `query` released from there can stay controlled across fast keystrokes and swallow them. The
	// capture phase runs before the field's own handler: both updates land in the same render.
	const handleIdentifierInput = React.useCallback(() => {
		setIdentifierQuery(undefined);
	}, []);

	const handleIdentifierClear = React.useCallback(() => {
		setIdentifierQuery(undefined);
		setFormData((current) => current.setIdentifier(''));
	}, []);

	const handleLabelChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLabel(value));
	}, []);

	const handleUsagesChange = React.useCallback((value: readonly string[]) => {
		setFormData((current) => current.setUsages([...value]));
	}, []);

	const handleApiVersionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setApiVersion(value));
	}, []);

	const handleFamilyChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setFamily(value));
	}, []);

	const handleDeliveryModeChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setDeliveryMode(value));
	}, []);

	const handleTranscriptionChunkingChange = React.useCallback((value: boolean) => {
		setFormData((current) => current.setTranscriptionChunking(value));
	}, []);

	const handleTranscriptionChunkMaxSecondsChange = React.useCallback((value: number | null) => {
		setFormData((current) =>
			current.setTranscriptionChunkMaxSeconds(value === null ? '' : String(value)),
		);
	}, []);

	const handleTranscriptionConcurrencyChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setTranscriptionConcurrency(value === null ? '' : String(value)));
	}, []);

	const hasNonAudioUsage = React.useMemo(() => {
		return formData.usages.some((usage) => !AUDIO_USAGES.includes(usage));
	}, [formData.usages]);

	const chunkMaxSeconds = React.useMemo(() => {
		return formData.transcriptionChunkMaxSeconds === ''
			? null
			: Number(formData.transcriptionChunkMaxSeconds);
	}, [formData.transcriptionChunkMaxSeconds]);

	const concurrency = React.useMemo(() => {
		return formData.transcriptionConcurrency === '' ? null : Number(formData.transcriptionConcurrency);
	}, [formData.transcriptionConcurrency]);

	const handleInputPriceChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setInputPricePerMillionTokens(toPriceInput(value)));
	}, []);

	const handleOutputPriceChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setOutputPricePerMillionTokens(toPriceInput(value)));
	}, []);

	const handleCachedInputPriceChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setCachedInputPricePerMillionTokens(toPriceInput(value)));
	}, []);

	const handleCacheWritePriceChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setCacheWritePricePerMillionTokens(toPriceInput(value)));
	}, []);

	const handleAudioMinutePriceChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setPricePerAudioMinute(toPriceInput(value)));
	}, []);

	const handleBilledPerStartedMinuteChange = React.useCallback((value: boolean) => {
		setFormData((current) => current.setBilledPerStartedMinute(value));
	}, []);

	const handleProviderOptionsChange = React.useCallback((value: string) => {
		setFormData((current) => current.setProviderOptions(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppMain` never mounts this section without an account.
		if (user.account === null) {
			return;
		}

		// Narrowed by `isFormDataValid()` above: the `providerId` constraint rejects `null`.
		if (formData.providerId === null) {
			return;
		}

		const accountId = user.account.id;
		const providerId = formData.providerId;
		const commandData = formData.toCommandData();

		setIsSubmitting(true);

		if (modelId === null) {
			dispatch(
				createAiModel(
					new CreateAiModel(
						accountId,
						providerId,
						commandData.identifier,
						commandData.label,
						commandData.usages,
						commandData.apiVersion,
						commandData.family,
						commandData.providerOptions,
						commandData.deliveryMode,
						commandData.transcriptionChunking,
						commandData.transcriptionChunkMaxSeconds,
						commandData.transcriptionConcurrency,
						commandData.inputPricePerMillionTokens,
						commandData.outputPricePerMillionTokens,
						commandData.cachedInputPricePerMillionTokens,
						commandData.cacheWritePricePerMillionTokens,
						commandData.pricePerAudioMinute,
						commandData.billedPerStartedMinute,
					),
				),
			)
				.then(() => closeModelSidebar())
				.catch((action: AiModelCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'AI_MODEL_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateAiModel(
				new UpdateAiModel(
					accountId,
					modelId,
					commandData.identifier,
					commandData.label,
					commandData.usages,
					commandData.apiVersion,
					commandData.family,
					commandData.providerOptions,
					commandData.deliveryMode,
					commandData.transcriptionChunking,
					commandData.transcriptionChunkMaxSeconds,
					commandData.transcriptionConcurrency,
					commandData.inputPricePerMillionTokens,
					commandData.outputPricePerMillionTokens,
					commandData.cachedInputPricePerMillionTokens,
					commandData.cacheWritePricePerMillionTokens,
					commandData.pricePerAudioMinute,
					commandData.billedPerStartedMinute,
					commandData.azureSpeechSettings,
				),
			),
		)
			.then(() => closeModelSidebar())
			.catch((action: AiModelUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action: mapAzureSpeechFieldErrors(action),
					eventNames: 'AI_MODEL_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, modelId, user, dispatch]);

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeModelSidebar}
			position="right"
			header={modelId === null ? 'Nouveau modèle' : 'Modifier le modèle'}
			footer={
				<React.Fragment>
					<Button type="button" severity="secondary" label="Annuler" onClick={closeModelSidebar} />
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={modelId === null ? 'Ajouter un modèle' : 'Modifier un modèle'}
			>
				<Dropdown
					name="providerId"
					label="Fournisseur"
					required
					disabled={modelId !== null}
					subLabel={
						modelId === null
							? undefined
							: 'Le fournisseur ne peut pas être modifié : supprimez ce modèle puis déclarez-le à nouveau pour en changer.'
					}
					options={providers?.list ?? []}
					optionLabel="label"
					optionValue="id"
					value={formData.providerId}
					onChange={handleProviderIdChange}
					errorMessage={formData.getValidationMessage('providerId')}
					isFormFieldValid={formData.isValid('providerId')}
				/>
				<div css={contentsStyle} onInputCapture={handleIdentifierInput}>
					<Autocomplete
						name="identifier"
						label="Identifiant du modèle"
						subLabel={identifierHelp}
						required
						dropdown
						dropdownMode="current"
						delay={0}
						suggestions={modelSuggestions}
						query={identifierQuery}
						value={formData.identifier}
						onChange={handleIdentifierChange}
						onSearch={handleIdentifierSearch}
						onClear={handleIdentifierClear}
						errorMessage={formData.getValidationMessage('identifier')}
						isFormFieldValid={formData.isValid('identifier')}
					/>
				</div>
				<InputText
					name="label"
					label="Nom"
					required
					value={formData.label}
					onChange={handleLabelChange}
					errorMessage={formData.getValidationMessage('label')}
					isFormFieldValid={formData.isValid('label')}
				/>
				{selectedProvider?.kind.value === AZURE_OPENAI_KIND ? (
					<InputText
						name="apiVersion"
						label="Version d’API (surcharge)"
						subLabel="Facultatif : laissez vide pour utiliser la version d’API du fournisseur."
						value={formData.apiVersion}
						onChange={handleApiVersionChange}
						errorMessage={formData.getValidationMessage('apiVersion')}
						isFormFieldValid={formData.isValid('apiVersion')}
					/>
				) : null}
				<MultiSelect
					name="usages"
					label="Usages autorisés"
					required
					options={aiUsages ?? []}
					optionLabel="label"
					optionValue="value"
					value={formData.usages}
					onChange={handleUsagesChange}
					errorMessage={formData.getValidationMessage('usages')}
					isFormFieldValid={formData.isValid('usages')}
				/>
				<ConditionalLoader data={hasAudioUsage}>
					<Dropdown
						name="family"
						label="Famille du modèle"
						required
						loading={aiModelFamilies === null}
						options={aiModelFamilies ?? []}
						optionLabel="label"
						optionValue="value"
						value={formData.family}
						onChange={handleFamilyChange}
						errorMessage={formData.getValidationMessage('family')}
						isFormFieldValid={formData.isValid('family')}
					/>
					<Dropdown
						name="deliveryMode"
						label="Mode d’envoi"
						subLabel="Temps réel : le traitement rejoue l’enregistrement dans le flux temps réel du modèle, plus vite que le temps réel. Réservé à Voxtral (Mistral ou Compatible OpenAI) et à OpenAI Transcribe (OpenAI ou Azure OpenAI)."
						required
						loading={aiTranscriptionDeliveryModes === null}
						options={aiTranscriptionDeliveryModes ?? []}
						optionLabel="label"
						optionValue="value"
						optionDisabled={isDeliveryModeOptionDisabled}
						value={formData.deliveryMode}
						onChange={handleDeliveryModeChange}
						errorMessage={formData.getValidationMessage('deliveryMode')}
						isFormFieldValid={formData.isValid('deliveryMode')}
					/>
					<FormSection title="Découpage de l’audio">
						<Checkbox
							name="transcriptionChunking"
							label="Découper l’audio en morceaux"
							checked={formData.transcriptionChunking}
							onChange={handleTranscriptionChunkingChange}
							errorMessage={formData.getValidationMessage('transcriptionChunking')}
							isFormFieldValid={formData.isValid('transcriptionChunking')}
						/>
						<InputNumber
							name="transcriptionChunkMaxSeconds"
							label="Durée maximale d’un morceau (secondes)"
							placeholder="Valeur du serveur"
							subLabel={
								isAzureSpeechFamily
									? 'De 60 à 7 200 secondes : un entretien plus court qu’un morceau part en un seul appel.'
									: undefined
							}
							min={60}
							max={isAzureSpeechFamily ? AZURE_SPEECH_CHUNK_MAX_SECONDS_MAX : 1200}
							useGrouping={false}
							disabled={!formData.transcriptionChunking}
							value={chunkMaxSeconds}
							onChange={handleTranscriptionChunkMaxSecondsChange}
							errorMessage={formData.getValidationMessage('transcriptionChunkMaxSeconds')}
							isFormFieldValid={formData.isValid('transcriptionChunkMaxSeconds')}
						/>
						<InputNumber
							name="transcriptionConcurrency"
							label="Appels en parallèle"
							subLabel="Plafonné par le réglage du serveur."
							placeholder="Valeur du serveur"
							min={1}
							max={10}
							useGrouping={false}
							disabled={!formData.transcriptionChunking}
							value={concurrency}
							onChange={handleTranscriptionConcurrencyChange}
							errorMessage={formData.getValidationMessage('transcriptionConcurrency')}
							isFormFieldValid={formData.isValid('transcriptionConcurrency')}
						/>
					</FormSection>
				</ConditionalLoader>
				<ConditionalLoader data={isAzureSpeechFamily && formData.hasAzureSpeechSettings}>
					<AzureSpeechSettingsSection formData={formData} setFormData={setFormData} />
				</ConditionalLoader>
				<ConditionalLoader data={isAzureSpeechFamily && modelId === null}>
					<Message
						severity="info"
						text="Les réglages Microsoft par défaut seront appliqués à l’enregistrement ; rouvrez la fiche pour les modifier."
					/>
				</ConditionalLoader>
				<FormSection title="Tarifs (en euros, hors taxes)">
					<p css={helpStyle}>
						Un modèle sans tarif produit un coût non renseigné. Un tarif modifié ne s’applique
						qu’aux appels suivants. Sans prix de cache, les jetons lus ou écrits en cache sont
						comptés au prix d’entrée.
					</p>
					<ConditionalLoader data={hasNonAudioUsage}>
						<InputNumber
							name="inputPricePerMillionTokens"
							label="Prix pour un million de jetons en entrée"
							placeholder="Non renseigné"
							min={0}
							max={100000}
							maxFractionDigits={6}
							suffix=" €"
							value={toPriceNumber(formData.inputPricePerMillionTokens)}
							onChange={handleInputPriceChange}
							errorMessage={formData.getValidationMessage('inputPricePerMillionTokens')}
							isFormFieldValid={formData.isValid('inputPricePerMillionTokens')}
						/>
						<InputNumber
							name="outputPricePerMillionTokens"
							label="Prix pour un million de jetons en sortie"
							placeholder="Non renseigné"
							min={0}
							max={100000}
							maxFractionDigits={6}
							suffix=" €"
							value={toPriceNumber(formData.outputPricePerMillionTokens)}
							onChange={handleOutputPriceChange}
							errorMessage={formData.getValidationMessage('outputPricePerMillionTokens')}
							isFormFieldValid={formData.isValid('outputPricePerMillionTokens')}
						/>
						<InputNumber
							name="cachedInputPricePerMillionTokens"
							label="Prix pour un million de jetons lus en cache"
							placeholder="Non renseigné"
							min={0}
							max={100000}
							maxFractionDigits={6}
							suffix=" €"
							value={toPriceNumber(formData.cachedInputPricePerMillionTokens)}
							onChange={handleCachedInputPriceChange}
							errorMessage={formData.getValidationMessage('cachedInputPricePerMillionTokens')}
							isFormFieldValid={formData.isValid('cachedInputPricePerMillionTokens')}
						/>
						<InputNumber
							name="cacheWritePricePerMillionTokens"
							label="Prix pour un million de jetons écrits en cache"
							placeholder="Non renseigné"
							min={0}
							max={100000}
							maxFractionDigits={6}
							suffix=" €"
							value={toPriceNumber(formData.cacheWritePricePerMillionTokens)}
							onChange={handleCacheWritePriceChange}
							errorMessage={formData.getValidationMessage('cacheWritePricePerMillionTokens')}
							isFormFieldValid={formData.isValid('cacheWritePricePerMillionTokens')}
						/>
					</ConditionalLoader>
					<ConditionalLoader data={hasAudioUsage}>
						<InputNumber
							name="pricePerAudioMinute"
							label="Prix d’une minute d’audio"
							placeholder="Non renseigné"
							min={0}
							max={100000}
							maxFractionDigits={6}
							suffix=" €"
							value={toPriceNumber(formData.pricePerAudioMinute)}
							onChange={handleAudioMinutePriceChange}
							errorMessage={formData.getValidationMessage('pricePerAudioMinute')}
							isFormFieldValid={formData.isValid('pricePerAudioMinute')}
						/>
						<Checkbox
							name="billedPerStartedMinute"
							label="Facturé à la minute entamée"
							checked={formData.billedPerStartedMinute}
							onChange={handleBilledPerStartedMinuteChange}
							errorMessage={formData.getValidationMessage('billedPerStartedMinute')}
							isFormFieldValid={formData.isValid('billedPerStartedMinute')}
						/>
					</ConditionalLoader>
				</FormSection>
				<TextArea
					name="providerOptions"
					label="Options supplémentaires (JSON)"
					subLabel={
						modelId === null
							? undefined
							: 'Laissez ce champ vide pour conserver les options déjà enregistrées ; saisissez {} pour les supprimer.'
					}
					rows={4}
					value={formData.providerOptions}
					onChange={handleProviderOptionsChange}
					errorMessage={formData.getValidationMessage('providerOptions')}
					isFormFieldValid={formData.isValid('providerOptions')}
				/>
			</Form>
		</Sidebar>
	);
};

export default ModelSidebar;
